//framework
import React, { Component } from 'react';
import * as Colyseus from "@colyseus/sdk";
import { Route } from 'react-router';
import { animateCSS } from 'utils/css';
import { audio } from 'services/audioManager';
import { createSeededRandoms } from 'utils/seededRandom';
import NoSleep from 'nosleep.js';

//styles
import styles from './Host.module.scss';
import "animate.css";

//components
import Loading from 'components/Shared/Loading/Loading';
import OptionsMenu from "components/Shared/OptionsMenu/OptionsMenu";
import RoomDetails from 'components/Host/SubComponents/RoomDetails/RoomDetails';
import ConnectingOverlay from 'components/Host/Views/ConnectingOverlay/ConnectingOverlay';
import DebugPanel from 'components/Shared/DebugPanel/DebugPanel';
import QuestionView from 'components/Host/Views/QuestionView/QuestionView';
import RevealView from 'components/Host/Views/RevealView/RevealView';
import PlayersView from 'components/Host/Views/Players/PlayersView';
import GameOverView from 'components/Host/Views/GameOverView/GameOverView';
import Timer from 'components/Host/SubComponents/Timer/Timer';
import StartMenuView from 'components/Host/Views/StartMenu/StartMenu';
import RoundIntroView from 'components/Host/Views/RoundIntro/RoundIntro';
import VideoPlayer from 'components/Shared/VideoPlayer/VideoPlayer';

//constants
import { APP_TYPES } from 'constants/appTypes';
import { GAME_STATES, GAME_ID, MESSAGES, VIDEOS } from 'constants/constants';
import { isTerminalByCode, retryDelayForCode, isTerminalReconnectError } from 'services/connectionManager';
import { TEST_ROOM_STATE } from 'constants/testRoomState';

//dev settings
const useTestState = false && process.env.REACT_APP_ENVIRONMENT === "development";
const preventRedirect = false && process.env.REACT_APP_ENVIRONMENT === "development";

const STORAGE_KEY = 'mdmd_reconnection_token';

/**
 * Host Component
 * @param {Object} props
 * @param {boolean} props.isLoadingComplete - Whether preloading is complete
 */
export class Host extends Component {
    static displayName = Host.name;

    constructor(props) {
        super(props);

        this.client = new Colyseus.Client(process.env.REACT_APP_GAME_SERVER_URL);
        this.noSleep = new NoSleep();
        this.state = {
            roomId: 0,
            room: null,
            myId: null,
            roomState: useTestState ? TEST_ROOM_STATE : null,
            playersArray: [],
            redirect: null,
            menuOpen: false,
            gameState: GAME_STATES.LOADING,
            gameBegun: false,
            connected: false,
            showReconnectPopup: false,
            reconnectionToken: null,
            hostLoadedSent: false,
            isMirror: false,
        };

        this.reconnectTimer = null;
        this.videoPlayerRef = React.createRef();
        this.toggleMute = this.toggleMute.bind(this);
        this.toggleMenu = this.toggleMenu.bind(this);
        this.handleVideoEnd = this.handleVideoEnd.bind(this);
        this.handleVideoError = this.handleVideoError.bind(this);
    }

    componentDidMount() {
        window.addEventListener('offline', this.handleOffline);
        window.addEventListener('online', this.handleOnline);

        // Enable wake lock to prevent screen from sleeping
        try {
            this.noSleep.enable();
            console.log('NoSleep enabled');
        } catch (err) {
            console.warn('NoSleep enable failed:', err);
        }

        this.doReconnect();
    }

    componentDidUpdate(prevProps, prevState) {
        // Check if we should send HOST_LOADED when loading completes or connection is established
        if ((prevProps.isLoadingComplete !== this.props.isLoadingComplete ||
            prevState.connected !== this.state.connected) &&
            !prevState.hostLoadedSent) {
            this.sendHostLoadedIfReady();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('offline', this.handleOffline);
        window.removeEventListener('online', this.handleOnline);
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer);

        // Disable wake lock when component unmounts
        if (this.noSleep && this.noSleep.isEnabled) {
            this.noSleep.disable();
        }
    }

    sendIfPrimary = (message, data = {}) => {
        if (this.state.isMirror) return;
        this.state.room?.send(message, data);
    };

    get isPrimary() { return !this.state.isMirror; }

    sendHostLoadedIfReady = () => {
        const { isLoadingComplete } = this.props;
        const { connected, hostLoadedSent, room } = this.state;

        // Only send if both conditions are met, we haven't sent it yet, and we have a room state
        if (connected && isLoadingComplete && !hostLoadedSent && room) {
            console.log('Sending HOST_LOADED message');
            this.sendIfPrimary(MESSAGES.TO_SERVER.HOST_LOADED, { gameId: GAME_ID });
            this.setState({ hostLoadedSent: true });
        }
    };

    handleOffline = () => {
        // Clean up the old room connection. Disable SDK auto-retry so it doesn't
        // race with handleOnline → scheduleReconnect.
        if (this.state.room) {
            try {
                this.state.room.reconnection.maxRetries = 0;
                this.state.room.removeAllListeners(); // Important!
                this.state.room.leave(false);
            } catch { }
        }
        audio.stopAllAudio();
        this.setState({ connected: false, showReconnectPopup: true, room: null, hostLoadedSent: false });
        this.scheduleReconnect(1000);
    };

    handleOnline = () => {
        // Force clean slate before reconnecting
        this.setState({ connected: false, room: null, hostLoadedSent: false }, () => {
            this.scheduleReconnect(0);
        });
    };

    /** Robust token getter: URL -> state -> localStorage */
    getReconnectionToken = () => {
        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get('token');
        if (fromUrl) return fromUrl;
        if (this.state.reconnectionToken) return this.state.reconnectionToken;
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) return stored;
        } catch { }
        return null;
    };

    /** Save token everywhere we can (URL + state + localStorage) */
    setReconnectionToken = (token) => {
        if (!token) return;
        this.setState({ reconnectionToken: token });
        try { localStorage.setItem(STORAGE_KEY, token); } catch { }
        this.updateTokenInUrl(token);
    };

    updateTokenInUrl(token) {
        try {
            const url = new URL(window.location.href);
            url.searchParams.set('token', token);
            window.history.replaceState(null, '', url.toString());
        } catch (e) {
            console.warn(e);
        }
    }

    animateAvatar(id, animation) {
        if (document.getElementById(`avatar-${id}`)) {
            animateCSS(`#avatar-${id}`, animation);
        }
    }

    toggleMenu() {
        this.setState(s => ({ menuOpen: !s.menuOpen }));
    }

    toggleMute() {
        const mdmdData = this.state.roomState?.mdmdData;
        if (!mdmdData || !this.state.room) return;
        const muted = !!mdmdData.mutedMusic && !!mdmdData.mutedSfx;
        this.sendIfPrimary(MESSAGES.TO_SERVER.MUTE_MUSIC, { mute: !muted });
        this.sendIfPrimary(MESSAGES.TO_SERVER.MUTE_SFX, { mute: !muted });
    }

    handleVideoEnd() {
        const gameState = this.state.gameState;
        switch (gameState) {
            case GAME_STATES.INTRO_VIDEO:
                this.sendIfPrimary(MESSAGES.TO_SERVER.INTRO_VIDEO_COMPLETE, { gameId: GAME_ID });
                return;
            case GAME_STATES.GAME_START_VIDEO:
                this.sendIfPrimary(MESSAGES.TO_SERVER.GAME_START_VIDEO_COMPLETE, { gameId: GAME_ID });
                return;
            default:
                return;
        }
    }

    handleVideoError(errorDetails) {
        console.error('Video failed to load:', errorDetails);
        // Optionally skip to next state or handle error
        // For example, you could automatically advance past the video:
        // this.handleVideoEnd();
    }

    getRedirectURL(display = false) {
        let url = display ? process.env.REACT_APP_GAME_CITY_URL_DISPLAY : process.env.REACT_APP_GAME_CITY_URL;
        if (this.state.room) {
            if (this.state.room.name !== "game_city_room") {
                url = display ? process.env.REACT_APP_GAME_CITY_URL_DISPLAY : process.env.REACT_APP_GAME_CITY_URL;
            }
        }
        return url;
    }

    goToLobby = () => {
        let platformPath = "/lobby";
        if (this.state.roomState?.platform === APP_TYPES.ROKU) platformPath = "/roku";
        else if (this.state.roomState?.platform === APP_TYPES.FIRE_TV) platformPath = "/firetv";

        const token = this.getReconnectionToken();
        let tokenPart;
        if (this.state.isMirror) {
            tokenPart = token ? `?mirror=${this.state.roomId}&token=${encodeURIComponent(token)}` : "";
        } else {
            tokenPart = token ? `?token=${encodeURIComponent(token)}` : "";
        }

        this.setState({ redirect: `${this.getRedirectURL()}${platformPath}${tokenPart}` });

        if (this.locationCheckInterval) clearInterval(this.locationCheckInterval);
        // Disable SDK auto-retry before closing the WS; leave(false) alone would
        // trigger the SDK's built-in reconnection loop and re-enter the now-lobby
        // room, causing a reconnect/leave cycle.
        try {
            if (this.state.room) {
                this.state.room.reconnection.maxRetries = 0;
                this.state.room.leave(false);
            }
        } catch { }
    };

    scheduleReconnect = (delayMs = 3000) => {
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
        this.reconnectTimer = setTimeout(() => this.doReconnect(), delayMs);
    };

    updateRoomSate = (roomState) => {
        // Initialize seeded random for deterministic audio (mirror sync)
        const seed = roomState.mdmdData?.randomSeed;
        if (seed && !this._seededRandomsInitialized) {
            this._seededRandomsInitialized = true;
            const rngs = createSeededRandoms(seed);
            audio.setSeededRandom(rngs.audio);
        }

        const newPlayers = Array.from(roomState.players?.values() ?? []);

        // Always update playersArray since Colyseus state objects are mutable
        // and isEqual won't detect property changes within the same object references
        this.setState({
            roomState: roomState,
            gameState: roomState.mdmdData.gameState,
            playersArray: newPlayers
        });
    }

    doReconnect = () => {
        const token = this.getReconnectionToken();
        const offline = typeof navigator !== "undefined" && navigator.onLine === false;
        const params = new URLSearchParams(window.location.search);
        const mirrorHint = params.get('mirror') === 'true';

        if (!token) {
            // We can't recover this session without a token.
            this.setState({ connected: false, showReconnectPopup: true });
            // Only send to lobby if we're online; otherwise, sit tight and retry occasionally.
            if (!offline) this.goToLobby();
            else this.scheduleReconnect(3000);
            return;
        }

        if (this.state.connected) return;

        this.client.reconnect(token).then((room) => {
            if (this.reconnectTimer) clearTimeout(this.reconnectTimer);

            this.setState({
                room,
                roomId: room.roomId,
                myId: room.sessionId,
                connected: true,
                showReconnectPopup: false,
                hostLoadedSent: false, // Reset when reconnecting
            });

            room.onStateChange.once((roomState) => {
                const isPrimaryHost = roomState.host.id === room.sessionId;

                if (isPrimaryHost) {
                    // Primary host: persist token and notify server
                    this.setReconnectionToken(room.reconnectionToken);
                    room.send("update_host_token", { reconnectionToken: room.reconnectionToken });
                } else {
                    // Secondary host (mirror): detect and set mirror state
                    console.warn("This client is not the primary host — entering mirror mode.");
                    this.setState({ isMirror: true });

                    // Update URL to preserve mirror hint on refresh
                    try {
                        const url = new URL(window.location.href);
                        url.searchParams.set('token', room.reconnectionToken);
                        url.searchParams.set('mirror', 'true');
                        window.history.replaceState(null, '', url.toString());
                    } catch (e) { console.warn(e); }

                    // Ask server to confirm mirror status
                    room.send("check_mirror_status", {});
                }

                this.updateRoomSate(roomState);
                this.sendHostLoadedIfReady();
            });

            room.onStateChange((roomState) => {
                this.updateRoomSate(roomState);
            });

            room.onMessage(MESSAGES.TO_CLIENTS.ANIMATE_POTATO, (message) => {
                this.animateAvatar(message.id, message.animation);
            });

            room.onMessage(MESSAGES.TO_CLIENTS.CHANGE_GAME, () => {
                // Explicit server instruction to leave → lobby.
                this.goToLobby();
            });

            room.onMessage("mirror_status", (message) => {
                if (message.isMirror) {
                    this.setState({ isMirror: true });
                }
            });

            room.onError((code, message) => {
                console.log(this.client.id, "room error", code, message);
            });

            room.onLeave((code) => {
                if (this.state.redirect) return;
                console.log("onLeave, code:", code)

                const nowOffline = typeof navigator !== "undefined" && navigator.onLine === false;

                // Only navigate on terminal codes *and* when online.
                if (!nowOffline && isTerminalByCode(code)) {
                    this.setState({ connected: false, showReconnectPopup: true });
                    this.goToLobby();
                } else {
                    const delay = retryDelayForCode(code);
                    this.setState({ connected: false, showReconnectPopup: true });
                    this.scheduleReconnect(delay);
                }
            });
        }).catch((e) => {
            console.error(e);
            const nowOffline = typeof navigator !== "undefined" && navigator.onLine === false;

            if (!nowOffline && isTerminalReconnectError(e)) {
                // If mirror hint is set, don't redirect — stay and retry
                if (mirrorHint) {
                    this.setState({ connected: false, showReconnectPopup: true });
                    this.scheduleReconnect(2000);
                    return;
                }
                this.goToLobby();
                return;
            }
            this.setState({ connected: false, showReconnectPopup: true });
            this.scheduleReconnect(2000);
        });
    };

    getVideoDef() {
        const { gameState } = this.state;
        if (gameState === GAME_STATES.INTRO_VIDEO || gameState === GAME_STATES.LOADING) {
            return VIDEOS.INTRO;
        }
        if (gameState === GAME_STATES.GAME_START_VIDEO || gameState === GAME_STATES.START_MENU) {
            return VIDEOS.GAME_START;
        }
        return null;
    }

    render() {
        if (this.state.redirect && !preventRedirect) {
            return (
                <React.Fragment>
                    <div id="gameContainer" className={styles.gameContainer}>
                        <div className={styles.loadingContainer}>
                            <Loading loadingText={"Sending you to the lobby!"} />
                        </div>
                    </div>
                    <div style={{ opacity: 0 }}>
                        <Route path="/" render={() => (window.location = this.state.redirect)} />
                    </div>
                </React.Fragment>
            )
        }

        const { room, roomState, connected, gameState } = this.state;
        const { mdmdData } = roomState || {};
        const players = this.state.playersArray ?? [];
        const videoDef = this.getVideoDef();
        return (
            <div>
                <div id="gameContainer" className={styles.gameContainer}>
                    <div className={styles.vignette} />
                    <ConnectingOverlay show={!connected || gameState === GAME_STATES.LOADING}
                        text={!connected ? (this.state.showReconnectPopup ? "disconnected, reconnecting" : "connecting") : "loading"} />

                    {connected && (
                        <>
                            {this.isPrimary && <OptionsMenu room={room} sendIfPrimary={this.sendIfPrimary} toggleMenu={this.toggleMenu} menuOpen={this.state.menuOpen} isHost={true} mutedMusic={mdmdData?.mutedMusic} mutedSfx={mdmdData?.mutedSfx}
                                gameState={gameState} />}

                            {
                                gameState !== GAME_STATES.LOADING && gameState !== GAME_STATES.INTRO_VIDEO &&
                                <RoomDetails url={this.getRedirectURL(false)} displayUrl={this.getRedirectURL(true)} mutedMusic={mdmdData?.mutedMusic} mutedSfx={mdmdData?.mutedSfx}
                                    roomId={this.state.roomId} onToggleMute={this.toggleMute} onToggleMenu={this.toggleMenu} platform={roomState?.platform}
                                    hideButtons={[APP_TYPES.ROKU, APP_TYPES.FIRE_TV].includes(roomState?.platform)} isMirror={this.state.isMirror} />
                            }

                            <Timer gameState={gameState} roomState={roomState} />

                            <StartMenuView gameState={gameState} room={room} players={players} sendIfPrimary={this.sendIfPrimary} isMirror={this.state.isMirror} />
                            <RoundIntroView gameState={gameState} room={room} players={players} roomState={roomState} sendIfPrimary={this.sendIfPrimary} />

                            {gameState !== GAME_STATES.LOADING && gameState !== GAME_STATES.INTRO_VIDEO &&
                                <PlayersView gameState={gameState} players={players} />
                            }

                            <QuestionView gameState={gameState} room={room} players={players} roomState={roomState} sendIfPrimary={this.sendIfPrimary} />
                            <RevealView gameState={gameState} room={room} players={players} roomState={roomState} sendIfPrimary={this.sendIfPrimary} />
                            <GameOverView gameState={gameState} room={room} players={players} roomState={roomState} sendIfPrimary={this.sendIfPrimary} isMirror={this.state.isMirror} />

                        </>
                    )}
                    <VideoPlayer ref={this.videoPlayerRef} className={styles.videoPlayer} show={gameState === GAME_STATES.INTRO_VIDEO || gameState === GAME_STATES.GAME_START_VIDEO}
                        video={videoDef} showSkipButton={this.isPrimary} onEnded={this.handleVideoEnd} onError={this.handleVideoError} />
                </div>

                {useTestState && (
                    <DebugPanel state={this.state} players={players} onSetState={(updates) => { this.setState(updates); this.updateRoomSate(updates.roomState) }} />
                )}
            </div>
        );
    }
}